import { OrderLineItem } from "../../../Checkout/OrderInterfaces";
import { CompanyModel } from "../../../Company/company.model";
import {
	PhysicalEntryRow,
	PhysicalPdfCompanyDetails,
	PhysicalPdfOrderSummary,
} from "./types";

function uniqueSorted(values: string[]): string[] {
	return [...new Set(values.filter(Boolean))].sort((a, b) =>
		a.localeCompare(b),
	);
}

/** Keep whole words on the next line instead of hyphenating mid-word. */
export function disableWordHyphenation(word: string): string[] {
	return [word];
}

export function getVertical(entry: {
	award?: string;
	ancestry?: { name: string }[];
}): string {
	return entry.ancestry?.[0]?.name ?? entry.award ?? "";
}

export function getProgram(hierarchy: string[]): string {
	return hierarchy[1] ?? hierarchy[0] ?? "";
}

export function getCategory(hierarchy: string[]): string {
	if (hierarchy.length <= 2) {
		return hierarchy[hierarchy.length - 1] ?? "";
	}
	return hierarchy.slice(2).join(" — ");
}

export function getClassification(hierarchy: string[]): string {
	return hierarchy.slice(1).join(" > ");
}

export function mapLineItemToPhysicalEntry(
	lineItem: OrderLineItem,
): PhysicalEntryRow {
	const entry = lineItem.entry;
	const hierarchy = entry.hierarchy ?? [];

	return {
		id: lineItem.entryId,
		title: entry.title || "",
		brand: entry.brand || "",
		entryType: entry.entryType || "",
		hierarchy,
		coverImage: entry.coverImage || "",
		physicalItemDescription: entry.physicalItemDescription || "",
		programHasPhysicalComponent: lineItem.programHasPhysicalComponent,
		qrCodeUrl: entry.qrCodeUrl || "",
		isCampaign: entry.isCampaign !== undefined ? entry.isCampaign : false,
		vertical: getVertical(entry),
		program: getProgram(hierarchy),
		category: getCategory(hierarchy),
		classification: getClassification(hierarchy),
	};
}

export function getPhysicalLineItems(
	lineItems: OrderLineItem[],
): OrderLineItem[] {
	return lineItems.filter((lineItem) => lineItem.programHasPhysicalComponent);
}

export function buildPhysicalEntries(
	lineItems: OrderLineItem[],
): PhysicalEntryRow[] {
	return getPhysicalLineItems(lineItems).map(mapLineItemToPhysicalEntry);
}

export function buildOrderSummary(
	orderId: number,
	entries: PhysicalEntryRow[],
): PhysicalPdfOrderSummary {
	return {
		orderId,
		verticals: uniqueSorted(entries.map((e) => e.vertical)),
		categories: uniqueSorted(entries.map((e) => e.category)),
		entryCount: entries.length,
		entries,
	};
}

export function buildCompanyDetails(
	company: CompanyModel,
): PhysicalPdfCompanyDetails {
	const addressLines = [
		company.name || "",
		company.address1 || "",
		company.address2 || "",
		[company.city, company.state, company.postalCode]
			.filter(Boolean)
			.join(", "),
		company.country || "",
	].filter((line) => line.trim().length > 0);

	return {
		companyId: Number(company.id) || 0,
		companyName: company.name || "",
		companyAddress1: company.address1 || "",
		companyAddress2: company.address2 || "",
		companyCity: company.city || "",
		companyCountry: company.country || "",
		companyPostalCode: company.postalCode || "",
		companyState: company.state || "",
		entryContactName: company.entryContactName || "",
		entryContactEmail: company.entryContactEmail || "",
		entryContactPhone: company.entryContactPhone || "",
		fromAddress: addressLines.join("\n"),
	};
}
